import { invalidateNavigationCache } from "~/features/navigation/redux/navigationThunks";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { DEBUG_LANGUAGE_STORAGE_KEY } from "~/utils/language/isDebugLanguageEnabled";
import { LOGOUT_SUCCESS_KEY, markLogoutSuccessful } from "~/features/auth/login/loginUtils";
import { clearRedirectionInfo, REDIRECT_TO_PRODUCT, setUpLoginPopUp } from "~/utils/redirectionUtil";
import { invalidateSession, removeGlobalSlotsCache } from "~/app/api/pageData";
import { clearSessionRuntimeState } from "~/domains/user/sessionRuntime";
import { getStore } from "~/app/store/getStore";
import { replaceSalesPartnerContext } from "~/domains/affiliate/redux/actions";

let sessionExpiryLogout: Promise<void> | undefined;

export const doLogout = (reloadPage = true, switchUser = false) => {
  clearSessionRuntimeState();
  getStore().dispatch(replaceSalesPartnerContext());
  const postLogout = () => {
    if (switchUser) {
      setUpLoginPopUp();
    } else {
      clearRedirectionInfo();
    }
    if (reloadPage) {
      window.location.reload();
    }
  };
  invalidateNavigationCache();
  clearSessionStorage();
  return fetch(resolveApiUrl("/logout"))
    .then(() => markLogoutSuccessful())
    .finally(() => {
      invalidateSession();
      // Login is a full-page redirect, so the fresh document drops the in-memory query cache
      // on its own; account-context switches are covered by prepareAccountContextTransition's
      // queryClient.clear(). Logout and session expiry are the two in-SPA seams we must clear.
      removeGlobalSlotsCache();
      postLogout();
    });
};

export const startSessionExpiryLogout = () => {
  if (sessionExpiryLogout) {
    return sessionExpiryLogout;
  }

  const logout = doLogout(false).catch(() => undefined);
  sessionExpiryLogout = logout;
  void logout.finally(() => {
    if (sessionExpiryLogout === logout) {
      sessionExpiryLogout = undefined;
    }
  });
  return logout;
};

export const waitForSessionExpiryLogout = () => sessionExpiryLogout ?? Promise.resolve();

function clearSessionStorage() {
  const keysToPreserve = [DEBUG_LANGUAGE_STORAGE_KEY, LOGOUT_SUCCESS_KEY, REDIRECT_TO_PRODUCT];
  const dataToPreserve = keysToPreserve.reduce(
    (acc, key) => {
      const value = window.sessionStorage.getItem(key);
      if (value) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );
  window.sessionStorage.clear();
  Object.entries(dataToPreserve).forEach(([key, value]) => {
    window.sessionStorage.setItem(key, value);
  });
}
